import React from "react";
/* =========================================================
   珂玺资本 — 官网注册用户认证（智库）
   useAuth / authFetch / AuthModal 暴露到 window
   ========================================================= */

const { useState, useEffect, useCallback } = React;

const STORE_KEY = "kexi_user";
const EVT = "kexi-auth-change";

function readAuth() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return { token: null, user: null };
    const parsed = JSON.parse(raw);
    return { token: parsed.token || null, user: parsed.user || null };
  } catch {
    return { token: null, user: null };
  }
}

function writeAuth(token, user) {
  if (token) localStorage.setItem(STORE_KEY, JSON.stringify({ token, user }));
  else localStorage.removeItem(STORE_KEY);
  window.dispatchEvent(new CustomEvent(EVT));
}

function useAuth() {
  const [auth, setAuth] = useState(readAuth);
  useEffect(() => {
    const on = () => setAuth(readAuth());
    window.addEventListener(EVT, on);
    window.addEventListener("storage", on);
    return () => {
      window.removeEventListener(EVT, on);
      window.removeEventListener("storage", on);
    };
  }, []);
  const logout = useCallback(() => writeAuth(null, null), []);
  return { token: auth.token, user: auth.user, logout };
}

/** 带用户 token 的 fetch；401 时自动登出 */
async function authFetch(url, opts = {}) {
  const { token } = readAuth();
  const headers = { ...(opts.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(url, { ...opts, headers });
  if (res.status === 401 && token) writeAuth(null, null);
  return res;
}

// -----------------------------------------------------------
// 注册 / 登录弹窗（沿用官网 modal 视觉语汇）
// -----------------------------------------------------------
function AuthModal({ open, onClose, lang }) {
  const isZh = lang === "cn";
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("login"); // login | register
  const [form, setForm] = useState({ email: "", password: "", display_name: "", company: "" });
  const [state, setState] = useState({ busy: false, error: null, notice: null });
  const [captchaOk, setCaptchaOk] = useState(false);
  window.useScrollLock(open);
  const submitRef = React.useRef(null);
  window.useModalKeys(open, { onClose, onSubmit: () => submitRef.current && submitRef.current() });

  useEffect(() => {
    if (open) { setState({ busy: false, error: null, notice: null }); setCaptchaOk(false); }
  }, [open, tab]);

  if (!open) return null;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  submitRef.current = () => submit();

  const submit = async (e) => {
    e && e.preventDefault();
    if (!captchaOk) {
      setState({ busy: false, error: isZh ? "请先完成人机验证" : "Please complete the captcha first", notice: null });
      return;
    }
    setState({ busy: true, error: null, notice: null });
    try {
      const path = tab === "login" ? "/api/auth/login" : "/api/auth/register";
      const payload = tab === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, display_name: form.display_name, company: form.company || null };
      const res = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error === "email_taken"
          ? (isZh ? "该邮箱已注册，请直接登录" : "Email already registered — please sign in")
          : data.error === "invalid_credentials"
            ? (isZh ? "邮箱或密码错误" : "Invalid email or password")
            : data.error === "account_pending"
              ? (isZh ? "账户审核中，通过后即可登录" : "Account pending approval — you can sign in once approved")
              : data.error === "account_rejected"
                ? (isZh ? "账户暂不可用，请联系珂玺资本" : "Account unavailable — please contact Kexi Capital")
                : (isZh ? "操作失败，请稍后再试" : "Something went wrong — please retry");
        setState({ busy: false, error: msg, notice: null });
        return;
      }
      // 注册：不自动登录，提示等待审核
      if (tab === "register" && data.pending) {
        setState({
          busy: false, error: null,
          notice: isZh
            ? "注册已提交，待珂玺资本审核通过后即可登录。"
            : "Registration submitted — you can sign in once Kexi Capital approves your account.",
        });
        return;
      }
      writeAuth(data.token, data.user);
      setState({ busy: false, error: null, notice: null });
      onClose();
    } catch (err) {
      setState({ busy: false, error: String(err), notice: null });
    }
  };

  // 已登录视图
  if (user) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <button className="close" onClick={onClose}>×</button>
          <div className="eyebrow">{isZh ? "已登录" : "Signed in"}</div>
          <h2 className="h-2" style={{ marginTop: 16, marginBottom: 8 }}>{user.display_name}</h2>
          <p className="body-mute" style={{ fontSize: 13, marginBottom: 8 }}>{user.email}</p>
          {user.company && <p className="body-mute" style={{ fontSize: 13, marginBottom: 8 }}>{user.company}</p>}
          <p className="body-mute" style={{ fontSize: 13, marginBottom: 28 }}>
            {isZh ? "您可以在「智库」页面上传资料、查看您的报告。" : "Upload materials and view your reports on the Studio page."}
          </p>
          <button className="btn" style={{ width: "100%", justifyContent: "center" }} onClick={onClose}>
            {isZh ? "继续浏览" : "Continue"}<span className="arr"></span>
          </button>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 24, fontSize: 12 }}>
            <a style={{ cursor: "pointer", color: "var(--mute)" }} onClick={() => window.requestLogout(() => logout())}>
              {isZh ? "退出登录" : "Sign out"}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose}>×</button>
        <div className="eyebrow">{isZh ? "珂玺账户" : "Kexi Account"}</div>
        <h2 className="h-2" style={{ marginTop: 16, marginBottom: 8 }}>
          {tab === "login" ? (isZh ? "登录" : "Sign in") : (isZh ? "注册" : "Create account")}
        </h2>
        <p className="body-mute" style={{ fontSize: 13, marginBottom: 28 }}>
          {isZh
            ? "注册后即可上传资料、查看专属调研报告。"
            : "Register to upload materials and access your research reports."}
        </p>

        {/* Tabs */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 28 }}>
          {[
            { v: "login", l: isZh ? "登录" : "Sign in" },
            { v: "register", l: isZh ? "注册" : "Register" },
          ].map((o) => (
            <button key={o.v} onClick={() => setTab(o.v)} style={{
              padding: "10px 14px",
              border: `1px solid ${tab === o.v ? "var(--gold)" : "var(--line)"}`,
              background: tab === o.v ? "rgba(201,168,107,0.06)" : "transparent",
              color: tab === o.v ? "var(--ivory)" : "var(--mute)",
              fontSize: 13, letterSpacing: "0.04em",
            }}>{o.l}</button>
          ))}
        </div>

        <form onSubmit={submit}>
          {tab === "register" && (
            <>
              <div className="form-field">
                <label>{isZh ? "姓名" : "Name"}</label>
                <input type="text" value={form.display_name} required
                  onChange={(e) => upd("display_name", e.target.value)} />
              </div>
              <div className="form-field">
                <label>{isZh ? "机构 / 公司（选填）" : "Company (optional)"}</label>
                <input type="text" value={form.company}
                  onChange={(e) => upd("company", e.target.value)} />
              </div>
            </>
          )}
          <div className="form-field">
            <label>{isZh ? "邮箱" : "Email"}</label>
            <input type="email" value={form.email} required
              onChange={(e) => upd("email", e.target.value)} />
          </div>
          <div className="form-field">
            <label>{isZh ? "密码" : "Password"}</label>
            <input type="password" value={form.password} required minLength={6}
              placeholder={tab === "register" ? (isZh ? "至少 6 位" : "Min 6 characters") : "••••••••"}
              onChange={(e) => upd("password", e.target.value)} />
          </div>

          {/* 拼图拖动人机验证 */}
          <div style={{ marginTop: 8, marginBottom: 4 }}>
            <window.SliderCaptcha lang={lang} passed={captchaOk} onPass={() => setCaptchaOk(true)} />
          </div>

          {state.error && (
            <div style={{ color: "#d66", fontSize: 13, marginBottom: 12 }}>{state.error}</div>
          )}
          {state.notice && (
            <div style={{ color: "var(--gold)", fontSize: 13, marginBottom: 12, lineHeight: 1.6 }}>{state.notice}</div>
          )}

          <button type="submit" className="btn" style={{ width: "100%", justifyContent: "center", marginTop: 12 }} disabled={state.busy || !captchaOk}>
            {state.busy
              ? (isZh ? "请稍候…" : "Please wait…")
              : tab === "login" ? (isZh ? "登录" : "Sign in") : (isZh ? "注册" : "Register")}
            <span className="arr"></span>
          </button>
        </form>

        <div style={{ display: "flex", justifyContent: "center", marginTop: 24, fontSize: 12, color: "var(--mute)" }}>
          {tab === "login" ? (
            <a style={{ cursor: "pointer", color: "var(--gold)" }} onClick={() => setTab("register")}>
              {isZh ? "没有账户？注册 →" : "No account? Register →"}
            </a>
          ) : (
            <a style={{ cursor: "pointer", color: "var(--gold)" }} onClick={() => setTab("login")}>
              {isZh ? "已有账户？登录 →" : "Have an account? Sign in →"}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/** 用当前 token 写回更新后的 user（个人资料修改后刷新本地缓存） */
function updateStoredUser(user) {
  const { token } = readAuth();
  if (token) writeAuth(token, user);
}

Object.assign(window, { useAuth, authFetch, AuthModal, updateStoredUser });
